import {
  AdditiveBlending,
  BoxGeometry,
  CylinderGeometry,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
  ShaderMaterial,
  SphereGeometry,
  Vector2,
  Vector3,
} from 'three'
import {ObjectOfMapInterface} from '@/types/types'
import {BaseGltfLoader} from './BaseGltfLoader'
import {IRepaintable, IUniformsUpdatable} from './interfaces/IObjectLoader'
import {UniformsUpdater} from './utils/UniformsUpdater'
import {AnimationManager} from './components/AnimationManager'
import {ORB_CONSTANTS} from '@/components/MapContext/ThreeJs/DataOfThreeJS/OrbLoader/OrbConstants'
import {
  orbFragmentShader,
  orbOuterFragmentShader,
  orbOuterVertexShader,
  orbVertexShader,
  tornadoLightningFragmentShader,
  tornadoLightningVertexShader,
  tornadoSmokeFragmentShader,
  tornadoSmokeVertexShader,
} from '@/components/MapContext/ThreeJs/DataOfThreeJS/OrbLoader/OrbShaders'

export interface OrbObject3D extends Object3D {
  orbData?: ObjectOfMapInterface
  hexId?: string
  topModelsGroup?: Group
  hitbox?: Mesh
  tornado?: Mesh
  tornadoLightning?: Mesh
  batteryGroup?: Group
  batteryChargeBar?: Mesh
  nextFlashTime?: number
  flashIntensity?: number
}

export default class OrbLoader
  extends BaseGltfLoader
  implements IUniformsUpdatable, IRepaintable
{
  private orbModel: Object3D | null = null
  private orbGeometry: SphereGeometry | null = null
  private orbMaterialTemplate: ShaderMaterial | null = null
  private orbModels: Map<string, Object3D> = new Map()
  private readonly animationManager: AnimationManager = new AnimationManager()

  private readonly _uniforms: {
    iTime: {value: number}
    uColorAdjust?: {value: Vector3}
    vCamPos?: {value: Vector3}
    orbColor?: {value: Vector3}
    useOrbColor?: {value: number}
  }

  /**
   * Получение uniforms (для обратной совместимости)
   */
  get uniforms(): {
    iTime: {value: number}
    uColorAdjust?: {value: Vector3}
    vCamPos?: {value: Vector3}
    orbColor?: {value: Vector3}
    useOrbColor?: {value: number}
  } {
    return this._uniforms
  }

  constructor() {
    super()

    this._uniforms = {
      iTime: {value: 0},
      uColorAdjust: {value: ORB_CONSTANTS.DEFAULT_COLOR_ADJUST.clone()},
    }

    const geometry = new SphereGeometry(
      ORB_CONSTANTS.BASE_ORB_GEOMETRY_RADIUS,
      ORB_CONSTANTS.BASE_ORB_GEOMETRY_SEGMENTS,
      ORB_CONSTANTS.BASE_ORB_GEOMETRY_SEGMENTS
    )
    const material = new ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: orbVertexShader,
      fragmentShader: orbFragmentShader,
      depthWrite: false,
      blending: AdditiveBlending,
      side: 2,
    })

    this.orbModel = new Mesh(geometry, material)
    this.orbModel.rotation.set(0, Math.PI, 0)
    this.orbModel.scale.set(1, 1, 1)

    this.orbGeometry = geometry
    this.orbMaterialTemplate = material
  }

  protected createLoadPromise(): Promise<void> {
    const orbFiles = [
      'models/orbs/Gold.glb',
      'models/orbs/Iron.glb',
      'models/orbs/Oil.glb',
      'models/orbs/Silver.glb',
      'models/orbs/Titanium.glb',
    ]

    return new Promise((resolve, reject) => {
      // let loadedCount = 0
      // const totalModels = orbFiles.length

      // orbFiles.forEach((filePath) => {
      //   this.loadGltfFile(
      //     filePath,
      //     (gltf) => {
      //       const model =
      //         gltf.scene.children.length > 0
      //           ? gltf.scene.children[0]
      //           : gltf.scene
      //       model.scale.set(
      //         ORB_CONSTANTS.MODEL_SCALE_X,
      //         ORB_CONSTANTS.MODEL_SCALE_Y,
      //         ORB_CONSTANTS.MODEL_SCALE_Z
      //       )
      //
      //       const fileName = filePath.split('/').pop() || ''
      //       const modelName = fileName.replace('.glb', '')
      //       const normalizedModelName =
      //         modelName === 'Titanium' ? 'Titan' : modelName
      //
      //       this.orbModels.set(normalizedModelName, model)
      //       loadedCount++
      //
      //       if (loadedCount === totalModels) {
      resolve()
      //       }
      //     },
      //     (error) => {
      //       reject(error)
      //     }
      //   )
      // })
    })
  }

  initRepaint(repaint: () => void): void {
    this.animationManager.initRepaint(repaint)
  }

  /**
   * Получение орбов по строковому ключу (для обратной совместимости)
   * Возвращает readonly Record для защиты от изменений извне
   */
  get orbs(): Readonly<Record<string, OrbObject3D>> {
    return this.getObjectsAsRecord<OrbObject3D>()
  }

  /**
   * Обновление uniforms всех орбов (вызывается из ThreeJsLayer перед рендерингом)
   */
  updateUniforms(currentTime: number): void {
    this.objectsMap.forEach((orb) => {
      const orbObj = orb as OrbObject3D

      UniformsUpdater.updateUniformsInGroup(orb, currentTime, [
        'iTime',
        'uTime',
      ])

      if (orbObj.tornado) {
        const smokeMat = orbObj.tornado.material as ShaderMaterial
        if (smokeMat.uniforms?.uTime) {
          smokeMat.uniforms.uTime.value = currentTime
        }
      }

      if (orbObj.tornadoLightning) {
        const lightningMat = orbObj.tornadoLightning.material as ShaderMaterial

        if (currentTime > (orbObj.nextFlashTime || 0)) {
          orbObj.flashIntensity = ORB_CONSTANTS.FLASH_INITIAL_INTENSITY
          orbObj.tornadoLightning.rotation.y = Math.random() * Math.PI * 2
          orbObj.nextFlashTime =
            currentTime +
            ORB_CONSTANTS.FLASH_MIN_INTERVAL +
            Math.random() * ORB_CONSTANTS.FLASH_MAX_INTERVAL
        }
        if ((orbObj.flashIntensity || 0) > 0) {
          orbObj.flashIntensity =
            (orbObj.flashIntensity || 0) - ORB_CONSTANTS.FLASH_DECAY_RATE
        }
        if (lightningMat.uniforms?.uTime) {
          lightningMat.uniforms.uTime.value = currentTime
        }
        if (lightningMat.uniforms?.uFlash) {
          lightningMat.uniforms.uFlash.value = Math.max(
            0,
            orbObj.flashIntensity || 0
          )
        }
      }

      if (orbObj.batteryChargeBar) {
        const chargeLevel =
          Math.sin(currentTime * ORB_CONSTANTS.CHARGE_ANIMATION_SPEED) * 0.5 +
          0.5
        orbObj.batteryChargeBar.scale.y = chargeLevel
        orbObj.batteryChargeBar.position.y = (chargeLevel - 1.0) * 0.5

        const chargeMat = orbObj.batteryChargeBar.material as MeshBasicMaterial
        chargeMat.color.setHSL(0.5 * chargeLevel, 1, 0.5)
      }
    })
  }

  clearAllOrbs(): void {
    this.clearAll()
  }

  /**
   * Остановка анимации перед очисткой
   */
  protected onBeforeClear(): void {
    this.animationManager.clearAll()
  }

  private createOrbGroup(
    orbData: ObjectOfMapInterface,
    isPreview: boolean = false
  ): OrbObject3D | null {
    if (!this.orbGeometry || !this.orbMaterialTemplate) {
      return null
    }

    const group = new Group() as OrbObject3D

    const level = orbData.level || 3
    let uBaseColor

    switch (level) {
      case 1:
        uBaseColor = {value: ORB_CONSTANTS.LEVEL_COLORS[1].clone()}
        break
      case 2:
        uBaseColor = {value: ORB_CONSTANTS.LEVEL_COLORS[2].clone()}
        break
      case 3:
      default:
        uBaseColor = {value: ORB_CONSTANTS.LEVEL_COLORS[3].clone()}
        break
    }

    const timeOffset = Math.random() * ORB_CONSTANTS.TIME_OFFSET_RANGE
    const outerTimeOffset =
      Math.random() * ORB_CONSTANTS.OUTER_TIME_OFFSET_RANGE

    const material = new ShaderMaterial({
      uniforms: {
        iTime: {value: 0},
        iResolution: {
          value: new Vector2(window.innerWidth, window.innerHeight),
        },
        uColorAdjust: uBaseColor,
      },
      vertexShader: this.orbMaterialTemplate.vertexShader,
      fragmentShader: this.orbMaterialTemplate.fragmentShader,
    })

    material.userData.timeOffset = timeOffset

    const baseOrb = new Mesh(this.orbGeometry, material)
    // baseOrb.position.set(0, 1, 0)
    baseOrb.scale.set(
      ORB_CONSTANTS.BASE_ORB_SCALE,
      ORB_CONSTANTS.BASE_ORB_SCALE,
      ORB_CONSTANTS.BASE_ORB_SCALE
    )
    group.add(baseOrb)

    // --- Прозрачная сфера с молниями ----
    const outerGeometry = new SphereGeometry(
      ORB_CONSTANTS.OUTER_SPHERE_RADIUS,
      ORB_CONSTANTS.OUTER_SPHERE_SEGMENTS,
      ORB_CONSTANTS.OUTER_SPHERE_SEGMENTS
    )
    const outerMaterial = new ShaderMaterial({
      uniforms: {
        uTime: {value: 0},
      },
      vertexShader: orbOuterVertexShader,
      fragmentShader: orbOuterFragmentShader,
      transparent: true,
      blending: AdditiveBlending,
      side: 2,
      depthWrite: false,
    })
    outerMaterial.userData.timeOffset = outerTimeOffset

    const outerSphere = new Mesh(outerGeometry, outerMaterial)
    outerSphere.scale.set(
      ORB_CONSTANTS.OUTER_SPHERE_SCALE,
      ORB_CONSTANTS.OUTER_SPHERE_SCALE,
      ORB_CONSTANTS.OUTER_SPHERE_SCALE
    )
    // outerSphere.position.set(0, 1, 0)
    group.add(outerSphere)

    // if (this.orbModels.size > 0 && orbData.specifications) {
    //   const heightOffset = ORB_CONSTANTS.TOP_MODELS_HEIGHT_OFFSET

    //   const validSpecifications = Object.values(orbData.specifications).filter(
    //     (spec) => {
    //       if (!spec.source) return false
    //       return ORB_CONSTANTS.ORB_MODEL_NAMES.includes(spec.source.name as any)
    //     }
    //   )

    //   const modelCount = validSpecifications.length

    //   if (modelCount > 0) {
    //     const topModelsGroup = new Group()

    //     validSpecifications.forEach((spec) => {
    //       if (spec.source) {
    //         const modelName = spec.source.name
    //         if (ORB_CONSTANTS.ORB_MODEL_NAMES.includes(modelName as any)) {
    //           const sourceModel = this.orbModels.get(modelName)

    //           if (sourceModel) {
    //             const orbModel = sourceModel.clone()

    //             orbModel.position.set(0, heightOffset, 0)
    //             topModelsGroup.add(orbModel)
    //           }
    //         }
    //       }
    //     })

    //     group.add(topModelsGroup)
    //     group.topModelsGroup = topModelsGroup
    //   }
    // }

    // --- Создание торнадо (смерч) ---
    const smokeMat = new ShaderMaterial({
      uniforms: {uTime: {value: 0}},
      vertexShader: tornadoSmokeVertexShader,
      fragmentShader: tornadoSmokeFragmentShader,
      transparent: true,
      side: DoubleSide,
      depthWrite: false,
    })

    const tornado = new Mesh(
      new CylinderGeometry(
        ORB_CONSTANTS.TORNADO_TOP_RADIUS,
        ORB_CONSTANTS.TORNADO_BOTTOM_RADIUS,
        ORB_CONSTANTS.TORNADO_HEIGHT,
        ORB_CONSTANTS.TORNADO_SEGMENTS,
        1,
        true
      ),
      smokeMat
    )
    tornado.position.y = ORB_CONSTANTS.TORNADO_POSITION_Y
    group.add(tornado)
    group.tornado = tornado

    const tornadoBoltMat = new ShaderMaterial({
      uniforms: {uTime: {value: 0}, uFlash: {value: 0.0}},
      vertexShader: tornadoLightningVertexShader,
      fragmentShader: tornadoLightningFragmentShader,
      transparent: true,
      blending: AdditiveBlending,
      side: DoubleSide,
    })

    const tLightning = new Mesh(
      new CylinderGeometry(
        ORB_CONSTANTS.TORNADO_LIGHTNING_TOP_RADIUS,
        ORB_CONSTANTS.TORNADO_LIGHTNING_BOTTOM_RADIUS,
        ORB_CONSTANTS.TORNADO_LIGHTNING_HEIGHT,
        ORB_CONSTANTS.TORNADO_LIGHTNING_SEGMENTS,
        1,
        true
      ),
      tornadoBoltMat
    )
    tLightning.position.y = ORB_CONSTANTS.TORNADO_POSITION_Y
    group.add(tLightning)
    group.tornadoLightning = tLightning

    group.nextFlashTime = Math.random() * ORB_CONSTANTS.FLASH_MAX_INTERVAL
    group.flashIntensity = 0

    // --- Создание вертикальной батарейки ---
    const batteryGroup = new Group()
    batteryGroup.position.set(0, ORB_CONSTANTS.BATTERY_GROUP_POSITION_Y, 0)
    group.add(batteryGroup)
    group.batteryGroup = batteryGroup

    const shell = new Mesh(
      new CylinderGeometry(
        ORB_CONSTANTS.BATTERY_SHELL_RADIUS,
        ORB_CONSTANTS.BATTERY_SHELL_RADIUS,
        ORB_CONSTANTS.BATTERY_SHELL_HEIGHT,
        ORB_CONSTANTS.BATTERY_SHELL_SEGMENTS,
        1,
        true
      ),
      new MeshBasicMaterial({
        color: ORB_CONSTANTS.BATTERY_SHELL_COLOR,
        transparent: true,
        opacity: ORB_CONSTANTS.BATTERY_SHELL_OPACITY,
        side: DoubleSide,
      })
    )
    batteryGroup.add(shell)

    const capGeo = new CylinderGeometry(
      ORB_CONSTANTS.BATTERY_CAP_RADIUS,
      ORB_CONSTANTS.BATTERY_CAP_RADIUS,
      ORB_CONSTANTS.BATTERY_CAP_HEIGHT,
      ORB_CONSTANTS.BATTERY_CAP_SEGMENTS
    )
    const capMat = new MeshStandardMaterial({
      color: ORB_CONSTANTS.BATTERY_CAP_COLOR,
      metalness: 1,
      roughness: 0.3,
    })
    const topCap = new Mesh(capGeo, capMat)
    topCap.position.y = ORB_CONSTANTS.BATTERY_CAP_Y_OFFSET
    const botCap = new Mesh(capGeo, capMat)
    botCap.position.y = -ORB_CONSTANTS.BATTERY_CAP_Y_OFFSET
    batteryGroup.add(topCap, botCap)

    const chargeMat = new MeshBasicMaterial({
      color: ORB_CONSTANTS.BATTERY_CHARGE_COLOR,
      transparent: true,
      opacity: ORB_CONSTANTS.BATTERY_CHARGE_OPACITY,
    })
    const chargeBar = new Mesh(
      new CylinderGeometry(
        ORB_CONSTANTS.BATTERY_CHARGE_BAR_RADIUS,
        ORB_CONSTANTS.BATTERY_CHARGE_BAR_RADIUS,
        ORB_CONSTANTS.BATTERY_CHARGE_BAR_HEIGHT,
        ORB_CONSTANTS.BATTERY_CHARGE_BAR_SEGMENTS
      ),
      chargeMat
    )
    batteryGroup.add(chargeBar)
    group.batteryChargeBar = chargeBar

    if (!isPreview) {
      const hitboxGeometry = new BoxGeometry(
        ORB_CONSTANTS.HITBOX_SIZE,
        ORB_CONSTANTS.HITBOX_SIZE,
        ORB_CONSTANTS.HITBOX_SIZE
      )
      const hitboxMaterial = new MeshStandardMaterial({
        visible: false,
        transparent: true,
        opacity: 0,
      })
      const hitbox = new Mesh(hitboxGeometry, hitboxMaterial)
      hitbox.position.set(0, 0, 0)
      group.add(hitbox)
      group.hitbox = hitbox
    }

    return group
  }

  addOrb(
    center: [number, number],
    orbData: ObjectOfMapInterface,
    hexId: string
  ): OrbObject3D | null {
    if (!this.hasThreeLayer()) {
      return null
    }

    const group = this.createOrbGroup(orbData, false)
    if (!group) {
      return null
    }

    const addedGroup = this.threeLayer.addObject(
      hexId,
      group,
      center,
      ORB_CONSTANTS.ORB_ALTITUDE,
      ORB_CONSTANTS.ORB_SCALE
    ) as OrbObject3D

    addedGroup.orbData = orbData
    addedGroup.hexId = hexId
    addedGroup.name = `orb-${hexId}`
    addedGroup.topModelsGroup = group.topModelsGroup
    addedGroup.hitbox = group.hitbox

    this.addObjectToCollection(hexId, addedGroup)
    this.animationManager.addAnimatedObject(addedGroup)

    return addedGroup
  }

  removeOrb(hexId: string): void {
    const orb = this.getObject(hexId) as OrbObject3D | undefined
    if (!orb) return

    this.animationManager.removeAnimatedObject(orb)
    this.threeLayer?.removeObject(hexId)
    this.removeObjectFromCollection(hexId)
  }

  getOrbByHexId(hexId: string): OrbObject3D | null {
    return (this.getObject(hexId) as OrbObject3D) || null
  }

  showAllOrbs(): void {
    this.objectsMap.forEach((orb) => {
      orb.visible = true
    })
    const repaint = this.animationManager.getRepaint()
    repaint?.()
  }

  hideAllOrbs(): void {
    this.objectsMap.forEach((orb) => {
      orb.visible = false
    })
    const repaint = this.animationManager.getRepaint()
    repaint?.()
  }

  createPreviewOrb(
    center: [number, number],
    orbData: ObjectOfMapInterface
  ): Object3D | null {
    if (!this.threeLayer) {
      return null
    }

    const group = this.createOrbGroup(orbData, true)
    if (!group) {
      return null
    }

    const previewId = `preview-orb-${Date.now()}`
    return this.threeLayer.addObject(
      previewId,
      group,
      center,
      ORB_CONSTANTS.PREVIEW_ORB_ALTITUDE,
      ORB_CONSTANTS.PREVIEW_ORB_SCALE
    )
  }
}
